import { logError } from 'App/services/faroService';
import { getUserId } from 'App/services/idpTokenService';
import { VERPersonaToken, VERUserVerificationStatus } from 'Shared/models/swagger/ver';
import { setUserCacheData, UserSessionCacheKeys } from 'Shared/services/sessionCacheService';
import { SwrKeys } from 'Shared/services/swr/swrKeys';
import {
  getUserVerificationPersonaToken,
  getUserVerificationStatus
} from 'Shared/services/ver/api/verificationApi';
import useSWR, { useSWRConfig } from 'swr';

const userVerificationStatuses = {
  none      : 'NONE',
  mandated  : 'MANDATED',
  inProgress: 'IN_PROGRESS',
  passed    : 'PASSED',
  failed    : 'FAILED',
  email     : 'EMAIL'
} as const;

type UserVerificationStatus = (typeof userVerificationStatuses)[keyof typeof userVerificationStatuses];

/**
 *
 * @returns {Promise<StatusResponseModel>}
 */
const getStatusFromUserVerification = (optional = false): Promise<VERUserVerificationStatus | null> => {
  if (optional && !getUserId(true)) {
    return Promise.resolve(null);
  }

  return new Promise<VERUserVerificationStatus>((resolve, reject) => {
    getUserVerificationStatus().then((response) => {
      setUserCacheData(UserSessionCacheKeys.USER_VERIFICATION_STATUS, response.data);
      resolve(response.data);
    }).catch(error => {
      logError('getUserVerificationStatus', error);
      reject(null);
    });
  });
};

/**
 * Get Persona token from VER API to start the verification process
 * @returns {Promise<VERPersonaToken>}
 */
const getPersonaTokenFromUserVerification = (): Promise<VERPersonaToken> => {
  return new Promise<VERPersonaToken>((resolve, reject) => {
    getUserVerificationPersonaToken()
      .then((response) => {
        const personaTokenResponseModel = {
          token: response?.data?.token
        };

        resolve(personaTokenResponseModel);
      })
      .catch(error => {
        logError('getUserVerificationPersonaToken', error);
        reject(null);
      });
  });
};

/**
 * @returns {Promise<StatusResponseModel>}
 * @param optional
 */
const useUserVerificationStatus = (optional = false) => {
  return useSWR(SwrKeys.UserVerificationStatus, () => getStatusFromUserVerification(optional));
};

const useRefetchUserVerificationStatus = () => {
  const { mutate } = useSWRConfig();
  return () => mutate(key => key === SwrKeys.UserVerificationStatus);
};

export {
  getPersonaTokenFromUserVerification,
  getStatusFromUserVerification,
  useRefetchUserVerificationStatus,
  UserVerificationStatus,
  userVerificationStatuses,
  useUserVerificationStatus
};
